/**
 * Invoice Operations
 *
 * Platform-agnostic invoice CRUD operations.
 * These functions can be used by both web and mobile apps.
 *
 * @module @prosperia/shared/data/operations/invoices
 */

import {
  getSupabase,
  getCompanyId as sharedGetCompanyId,
  handleSupabaseError,
  withCompanyFilter,
} from '../../api/supabase';

/**
 * Resolve company ID from options or session
 * @private
 */
async function resolveCompanyId(options = {}) {
  const supabase = getSupabase();
  let companyId = options.companyId;

  if (!companyId && options.getCompanyId) {
    companyId = await options.getCompanyId();
  }

  if (!companyId && options.user) {
    companyId = await sharedGetCompanyId(options.user);
  }

  if (!companyId) {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (session?.user) {
      companyId = await sharedGetCompanyId(session.user);
    }
  }

  return companyId;
}

/**
 * Normalize invoice data - handle both camelCase and snake_case input
 * Converts everything to snake_case for database storage
 * Exported for tests.
 */
export function normalizeInvoiceData(data) {
  // FK links: keep an explicit null (unlink) but leave an absent key absent.
  // Defaulting them to null made every partial update (Mark Paid, Cancel)
  // clear client_id, job_id and estimate_id (#616).
  const link = (snake, camel) => (data[snake] !== undefined ? data[snake] : data[camel]);
  return {
    // Basic fields
    company_id: data.company_id ?? data.companyId,
    client_id: link('client_id', 'clientId'),
    job_id: link('job_id', 'jobId'),
    estimate_id: link('estimate_id', 'estimateId'),
    invoice_number: data.invoice_number ?? data.invoiceNumber,

    // Content fields
    title: data.title,
    description: data.description,
    notes: data.notes,
    items: data.items,
    status: data.status,

    // Customer fields
    customer_name: data.customer_name ?? data.customerName,
    customer_email: data.customer_email ?? data.customerEmail,
    customer_phone: data.customer_phone ?? data.customerPhone,
    customer_address: data.customer_address ?? data.customerAddress,

    // Financial fields
    subtotal: data.subtotal,
    total_amount: data.total_amount ?? data.totalAmount ?? data.total,
    tax_rate: data.tax_rate ?? data.taxRate,
    tax_amount: data.tax_amount ?? data.taxAmount,
    discount_amount: data.discount_amount ?? data.discountAmount,
    discount_type: data.discount_type ?? data.discountType,
    deposit_amount: data.deposit_amount ?? data.depositAmount,
    deposit_type: data.deposit_type ?? data.depositType,
    services_subtotal: data.services_subtotal ?? data.servicesSubtotal,
    client_payment_amount: data.client_payment_amount ?? data.clientPaymentAmount,
    processing_fees_amount: data.processing_fees_amount ?? data.processingFeesAmount,

    // Date fields
    due_date: data.due_date ?? data.dueDate,
    paid_at: data.paid_at ?? data.paidAt,
    sent_at: data.sent_at ?? data.sentAt,
    viewed_at: data.viewed_at ?? data.viewedAt,

    // Recurring fields
    is_recurring: data.is_recurring ?? data.isRecurring,
    recurring_frequency: data.recurring_frequency ?? data.recurringFrequency,
    recurring_interval_count: data.recurring_interval_count ?? data.recurringIntervalCount,
    recurring_start_date: data.recurring_start_date ?? data.recurringStartDate,
    recurring_end_date: data.recurring_end_date ?? data.recurringEndDate,
    recurring_next_run_date: data.recurring_next_run_date ?? data.recurringNextRunDate,
    recurring_parent_id: data.recurring_parent_id ?? data.recurringParentId,
    recurring_sequence: data.recurring_sequence ?? data.recurringSequence,
    recurring_require_approval: data.recurring_require_approval ?? data.recurringRequireApproval,

    // Rebill tracking
    rebilled_at: data.rebilled_at ?? data.rebilledAt,

    // Photo fields
    has_photos: data.has_photos ?? data.hasPhotos,
    before_photos: data.before_photos ?? data.beforePhotos,
    after_photos: data.after_photos ?? data.afterPhotos,

    // Other fields
    view_count: data.view_count ?? data.viewCount,
    pdf_url: data.pdf_url ?? data.pdfUrl,
    public_token: data.public_token ?? data.publicToken,
    payment_terms: data.payment_terms ?? data.paymentTerms,
    custom_terms: data.custom_terms ?? data.customTerms,
    custom_branding: data.custom_branding ?? data.customBranding,

    // System fields
    created_by: data.created_by ?? data.createdBy,
  };
}

/**
 * Clean normalized data - remove undefined values
 * @private
 */
function cleanData(data) {
  const cleaned = {};
  for (const [key, value] of Object.entries(data)) {
    if (value !== undefined) {
      cleaned[key] = value;
    }
  }
  return cleaned;
}

/**
 * Get all invoices for a company
 *
 * @param {Object} options - Options object
 * @param {string} options.companyId - Company ID (optional, will be resolved if not provided)
 * @param {Function} options.getCompanyId - Function to get company ID from context
 * @returns {Promise<Array>} Array of invoices or empty array on error
 */
export async function getInvoices(options = {}) {
  try {
    const supabase = getSupabase();
    const companyId = await resolveCompanyId(options);

    if (!companyId) {
      console.log('[invoices] No company ID available for getInvoices');
      return [];
    }

    const { data, error } = await supabase
      .from('invoices')
      .select(`
        *,
        client:clients(name, email, phone, sms_consent, marketing_sms_consent)
      `)
      .eq('company_id', companyId)
      .order('created_at', { ascending: false })
      .limit(500);

    if (error) {
      console.error('[invoices] Error fetching invoices:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('[invoices] getInvoices error:', error);
    handleSupabaseError(error, 'fetch invoices');
    return [];
  }
}

/**
 * Get a single invoice by ID
 *
 * @param {string} id - Invoice ID
 * @param {Object} options - Options object
 * @param {string} options.companyId - Company ID for filtering
 * @param {Object} options.user - User object for company ID resolution
 * @returns {Promise<Object|null>} Invoice data or null on error
 */
export async function getInvoice(id, options = {}) {
  try {
    const supabase = getSupabase();
    const companyId = await resolveCompanyId(options);

    if (!companyId) {
      console.error('[invoices] No company ID available for getInvoice');
      return null;
    }

    const { data, error } = await withCompanyFilter(
      supabase
        .from('invoices')
        .select(`
          *,
          client:clients(name, email, phone, sms_consent, marketing_sms_consent),
          job:jobs(title),
          estimate:estimates(estimate_number, final_price)
        `)
        .eq('id', id),
      companyId
    ).single();

    if (error) throw error;
    return data;
  } catch (error) {
    handleSupabaseError(error, 'get invoice');
    return null;
  }
}

/**
 * Get invoices by status
 *
 * @param {string} status - Invoice status (draft, sent, viewed, paid, overdue, cancelled)
 * @param {Object} options - Options object
 * @param {string} options.companyId - Company ID for filtering
 * @param {Function} options.getCompanyId - Function to get company ID from context
 * @returns {Promise<Array>} Array of invoices or empty array on error
 */
export async function getInvoicesByStatus(status, options = {}) {
  try {
    const supabase = getSupabase();
    const companyId = await resolveCompanyId(options);

    if (!companyId) {
      console.log('[invoices] No company ID available for getInvoicesByStatus');
      return [];
    }

    const { data, error } = await supabase
      .from('invoices')
      .select(`
        *,
        client:clients(name, email, phone, sms_consent, marketing_sms_consent)
      `)
      .eq('company_id', companyId)
      .eq('status', status)
      .order('created_at', { ascending: false })
      .limit(500);

    if (error) {
      console.error('[invoices] Error fetching invoices by status:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('[invoices] getInvoicesByStatus error:', error);
    handleSupabaseError(error, 'fetch invoices by status');
    return [];
  }
}

/**
 * Get invoices by client ID
 *
 * @param {string} clientId - Client ID
 * @param {Object} options - Options object
 * @param {string} options.companyId - Company ID for filtering
 * @param {Object} options.user - User object for company ID resolution
 * @param {Function} options.getCachedOrRateLimited - Caching function (optional)
 * @returns {Promise<Array>} Array of invoices or empty array on error
 */
export async function getInvoicesByClient(clientId, options = {}) {
  try {
    const supabase = getSupabase();
    const companyId = await resolveCompanyId(options);

    if (!companyId) {
      console.log('[invoices] No company ID available for getInvoicesByClient');
      return [];
    }

    const fetchInvoices = async () => {
      const { data, error } = await supabase
        .from('invoices')
        .select(`
          *,
          client:clients(name, email, phone, sms_consent, marketing_sms_consent)
        `)
        .eq('company_id', companyId)
        .eq('client_id', clientId)
        .order('created_at', { ascending: false })
        .limit(500);

      if (error) {
        console.error('[invoices] Error fetching invoices by client:', error);
        throw error;
      }

      return data || [];
    };

    // Use caching if provided, otherwise fetch directly
    if (options.getCachedOrRateLimited) {
      const cacheKey = `invoices_client_${clientId}_${companyId}`;
      return await options.getCachedOrRateLimited(cacheKey, fetchInvoices);
    }

    return await fetchInvoices();
  } catch (error) {
    console.error('[invoices] getInvoicesByClient error:', error);
    handleSupabaseError(error, 'fetch invoices by client');
    return [];
  }
}

/**
 * Get invoices by estimate ID
 *
 * @param {string} estimateId - Estimate ID
 * @param {Object} options - Options object
 * @param {string} options.companyId - Company ID for filtering
 * @returns {Promise<Array>} Array of invoices or empty array on error
 */
export async function getInvoicesByEstimate(estimateId, options = {}) {
  try {
    const supabase = getSupabase();
    const companyId = await resolveCompanyId(options);

    if (!companyId) {
      console.log('[invoices] No company ID available for getInvoicesByEstimate');
      return [];
    }

    const { data, error } = await supabase
      .from('invoices')
      .select(`
        *,
        client:clients(name, email, phone, sms_consent, marketing_sms_consent)
      `)
      .eq('company_id', companyId)
      .eq('estimate_id', estimateId)
      .order('created_at', { ascending: false })
      .limit(500);

    if (error) {
      console.error('[invoices] Error fetching invoices by estimate:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('[invoices] getInvoicesByEstimate error:', error);
    handleSupabaseError(error, 'fetch invoices by estimate');
    return [];
  }
}

/**
 * Generate the next invoice number for a company via the
 * `next_invoice_number` RPC. The RPC does an atomic year-scoped increment,
 * so concurrent callers are serialized at the row lock and the unique
 * (company_id, invoice_number) constraint cannot fire from this path.
 *
 * @param {ReturnType<typeof getSupabase>} supabase
 * @param {string} companyId
 * @returns {Promise<string>} e.g. "INV-2026-00001"
 */
async function generateInvoiceNumber(supabase, companyId) {
  if (!companyId) {
    throw new Error('companyId is required to generate an invoice number');
  }
  const { data, error } = await supabase.rpc('next_invoice_number', {
    p_company_id: companyId,
  });
  if (error) throw error;
  return data;
}

/**
 * Create a new invoice
 *
 * @param {Object} invoiceData - Invoice data to create
 * @param {Object} options - Options object
 * @param {string} options.companyId - Company ID (required)
 * @param {boolean} options.generateNumber - Auto-generate invoice number if not provided (default: true)
 * @param {Object} options.user - Pre-resolved user (skips getSession lookup)
 * @param {number} options.insertTimeout - Timeout for insert operation (default: 10000ms)
 * @returns {Promise<Object>} Created invoice data
 * @throws {Error} If authentication fails or timeout occurs
 */
export async function createInvoice(invoiceData, options = {}) {
  const insertTimeoutMs = options.insertTimeout || 10000;
  const shouldGenerateNumber = options.generateNumber !== false;

  console.log('[invoices] Starting createInvoice...');

  try {
    const supabase = getSupabase();

    // Resolve user from caller, otherwise fall back to getSession (no timeout
    // race — see clients.createClient for rationale).
    let sessionUser = options.user || null;

    if (!sessionUser) {
      console.log('[invoices] No user passed, calling getSession...');
      const {
        data: { session },
        error: authError,
      } = await supabase.auth.getSession();

      if (authError) {
        console.error('[invoices] Auth error:', authError);
        throw new Error(`Authentication error: ${authError.message}`);
      }

      if (!session?.user) {
        throw new Error('User authentication required to create invoice. Please sign in again.');
      }

      sessionUser = session.user;
    }

    // Resolve company ID
    let companyId = options.companyId || invoiceData.company_id || invoiceData.companyId;
    if (!companyId) {
      companyId = await sharedGetCompanyId(sessionUser);
    }

    if (!companyId) {
      throw new Error('No company ID found');
    }

    // Normalize and clean data
    const normalized = normalizeInvoiceData(invoiceData);
    const cleaned = cleanData(normalized);

    // Generate invoice number if not provided
    let invoiceNumber = cleaned.invoice_number;
    if (!invoiceNumber && shouldGenerateNumber) {
      invoiceNumber = await generateInvoiceNumber(supabase, companyId);
    }

    const invoiceDataWithUser = {
      ...cleaned,
      company_id: companyId,
      invoice_number: invoiceNumber,
      created_by: sessionUser.id,
    };

    console.log('[invoices] Inserting invoice into database...');

    // Add timeout to the insert operation
    const insertTimeout = new Promise((_, reject) => {
      setTimeout(
        () => reject(new Error(`Database insert timed out after ${insertTimeoutMs / 1000} seconds`)),
        insertTimeoutMs
      );
    });

    const { data, error } = await Promise.race([
      supabase.from('invoices').insert([invoiceDataWithUser]).select().single(),
      insertTimeout,
    ]);

    console.log('[invoices] Insert result:', { dataId: data?.id, error });

    if (error) {
      console.error('[invoices] Database error:', error);
      handleSupabaseError(error, 'create invoice');
      throw error;
    }

    if (!data) {
      throw new Error('No data returned from database');
    }

    console.log('[invoices] Invoice created successfully:', data.id);
    return data;
  } catch (error) {
    console.error('[invoices] Error in createInvoice:', error);
    throw error;
  }
}

/**
 * Update an existing invoice
 *
 * @param {string} id - Invoice ID
 * @param {Object} updates - Fields to update (accepts both camelCase and snake_case)
 * @param {Object} options - Options object
 * @param {string} options.companyId - Company ID for filtering
 * @param {Object} options.user - User object for company ID resolution
 * @returns {Promise<Object|null>} Updated invoice data or null on error
 */
export async function updateInvoice(id, updates, options = {}) {
  try {
    const supabase = getSupabase();
    const companyId = await resolveCompanyId(options);

    if (!companyId) {
      console.error('[invoices] No company ID available for updateInvoice');
      throw new Error('No company ID found');
    }

    // Validate invoice ID
    if (!id || typeof id !== 'string' || id.length < 30) {
      console.error('[invoices] Invalid invoice ID:', id);
      throw new Error(`Invalid invoice ID: ${id}`);
    }

    // Normalize and clean update data
    const normalized = normalizeInvoiceData(updates);
    const cleaned = cleanData(normalized);
    delete cleaned.company_id; // Never update company_id
    delete cleaned.created_by; // Never update created_by

    console.log('[invoices] Updating invoice:', id, 'with data:', cleaned);

    const { data, error } = await withCompanyFilter(
      supabase.from('invoices').update(cleaned).eq('id', id),
      companyId
    )
      .select()
      .single();

    if (error) {
      console.error('[invoices] Update error:', error);
      throw error;
    }

    console.log('[invoices] Update successful:', data?.id);
    return data;
  } catch (error) {
    handleSupabaseError(error, 'update invoice');
    return null;
  }
}

/**
 * Update invoice status only
 *
 * @param {string} id - Invoice ID
 * @param {string} status - New status
 * @param {Object} options - Options object
 * @param {string} options.companyId - Company ID for filtering
 * @returns {Promise<Object|null>} Updated invoice data or null on error
 */
export async function updateInvoiceStatus(id, status, options = {}) {
  try {
    const supabase = getSupabase();
    const companyId = await resolveCompanyId(options);

    if (!companyId) {
      console.error('[invoices] No company ID available for updateInvoiceStatus');
      throw new Error('No company ID found');
    }

    if (!id) {
      throw new Error('Invoice ID is required');
    }

    if (!status) {
      throw new Error('Status is required');
    }

    const { data, error } = await withCompanyFilter(
      supabase
        .from('invoices')
        .update({
          status,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id),
      companyId
    )
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    handleSupabaseError(error, 'update invoice status');
    return null;
  }
}

/**
 * Delete an invoice
 *
 * @param {string} id - Invoice ID
 * @param {Object} options - Options object
 * @param {string} options.companyId - Company ID for filtering
 * @param {Object} options.user - User object for company ID resolution
 * @returns {Promise<boolean>} True on success, false on error
 */
export async function deleteInvoice(id, options = {}) {
  try {
    const supabase = getSupabase();
    const companyId = await resolveCompanyId(options);

    if (!companyId) {
      console.error('[invoices] No company ID available for deleteInvoice');
      throw new Error('No company ID found');
    }

    const { error } = await withCompanyFilter(
      supabase.from('invoices').delete().eq('id', id),
      companyId
    );

    if (error) throw error;
    return true;
  } catch (error) {
    handleSupabaseError(error, 'delete invoice');
    return false;
  }
}
